/**
 * The rules that decide whether a truck check is ready to send.
 *
 * Not components, so they sit apart from the rest of the library, but they
 * belong to it for the same reason the components do: the phone and the desk
 * run the same checklist, and a question the phone calls unanswered has to be
 * unanswered on the desk too. They were written once for the driver app and
 * moved here the moment the office got its own run screen, before the two
 * copies could start to drift.
 *
 * The server checks all of this again on submit. These exist so the person
 * filling the form is told what is missing while they can still fix it,
 * instead of being handed a 400 at the end.
 */

export type AnswerStatus = 'PASS' | 'FAIL';

export type AnswerType =
  | 'PASS_FAIL'
  | 'NUMBER'
  | 'TEXT'
  | 'SELECT'
  | 'CHECKBOX'
  | 'YES_NO'
  | 'PHOTO';

export type PhotoRule = 'NONE' | 'ON_FAIL' | 'ALWAYS';

/**
 * The part of a checklist question these rules read.
 *
 * Deliberately smaller than either app's full item type. Both pass their own
 * richer shape straight in, and the generics below hand that shape back, so a
 * caller keeps its labels and help text.
 */
export interface ChecklistItem {
  id: string;
  answerType: AnswerType;
  isRequired: boolean;
  photoRule: PhotoRule;
  minValue?: number | null;
  maxValue?: number | null;
}

/** One question's half-filled answer, as it is being typed. */
export interface Draft {
  status?: AnswerStatus;
  /** Kept as the raw string so a half-typed number is not thrown away. */
  valueNumber?: string;
  valueText?: string;
  /** CHECKBOX only: the options that are ticked. */
  valueList: string[];
  note?: string;
  photoUrls: string[];
  /** How many photos are still going up. */
  uploading: number;
}

/**
 * An id for one run of a checklist, or for one photo waiting to go up.
 *
 * `crypto.randomUUID` exists only in a secure context, so it is missing on
 * `http://192.168.x.x` — a build served off a laptop to a phone in the yard,
 * which is exactly how a run screen gets tested. Calling it unguarded there
 * throws during render and white-screens the screen the driver is standing in
 * front of a truck to use. The fallback only has to be unique per device.
 */
export const newId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const emptyDraft = (): Draft => ({
  photoUrls: [],
  valueList: [],
  uploading: 0,
});

/** The draft for a question, or a blank one for a question nobody has touched. */
export const draftFor = (
  drafts: Record<string, Draft>,
  itemId: string
): Draft => drafts[itemId] ?? emptyDraft();

/** Whether this question has been given the thing it asks for. */
export const answered = (item: ChecklistItem, draft: Draft): boolean => {
  if (item.answerType === 'PASS_FAIL') return !!draft.status;
  if (item.answerType === 'NUMBER') return !!draft.valueNumber?.trim();
  if (item.answerType === 'CHECKBOX') return draft.valueList.length > 0;
  // The photo is the answer.
  if (item.answerType === 'PHOTO') return draft.photoUrls.length > 0;
  return !!draft.valueText?.trim();
};

/** Questions whose checklist demands a photo that is not there yet. */
export const missingPhotos = <T extends ChecklistItem>(
  items: T[],
  drafts: Record<string, Draft>
): T[] =>
  items.filter((item) => {
    const draft = draftFor(drafts, item.id);
    // A photo question with no photo is an unanswered question, and that is
    // already counted by answered().
    if (item.answerType === 'PHOTO') return false;
    if (item.photoRule === 'ALWAYS') return draft.photoUrls.length === 0;
    if (item.photoRule === 'ON_FAIL' && draft.status === 'FAIL') {
      return draft.photoUrls.length === 0;
    }
    return false;
  });

/** Numbers that are not numbers, or that sit outside what the question allows. */
export const outOfRange = <T extends ChecklistItem>(
  items: T[],
  drafts: Record<string, Draft>
): T[] =>
  items.filter((item) => {
    if (item.answerType !== 'NUMBER') return false;
    const raw = draftFor(drafts, item.id).valueNumber;
    if (!raw?.trim()) return false;
    const value = Number(raw);
    if (Number.isNaN(value)) return true;
    if (item.minValue != null && value < item.minValue) return true;
    if (item.maxValue != null && value > item.maxValue) return true;
    return false;
  });

/**
 * A fuel question's options, emptiest first, or null when they do not read
 * as a tank. Checklists list them fullest first ("Full, Three quarters, …,
 * Empty"), which is the right order for buttons and the wrong one for a dial.
 */
export function fuelLevelsFrom(options: readonly string[]): string[] | null {
  if (options.length < 2) return null;
  const isEmpty = (option: string) => /^(e|empty)$/i.test(option.trim());
  const isFull = (option: string) => /^(f|full)$/i.test(option.trim());
  const first = options[0];
  const last = options[options.length - 1];
  if (isEmpty(first) && isFull(last)) return [...options];
  if (isFull(first) && isEmpty(last)) return [...options].reverse();
  return null;
}
